import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, ExternalLink } from 'lucide-react';
import api from '../utils/api';
import toast from 'react-hot-toast';

/**
 * Blocks the app until the signed-in user has accepted the Terms of Use and Privacy Policy
 * currently in force.
 *
 * Why it exists: the acceptance endpoint was already there, took its version number from the
 * client, and was called by no gate — so there was no answer to "can you show this person agreed
 * to your terms, and to which version?". That is the question that matters if a customer disputes
 * the agreement, and accepting the Privacy Policy is also one of the two lawful bases under
 * APP 8 for sending personal information to the overseas AI providers the policy names.
 *
 * Deliberately NOT dismissible — no close button, no click-outside, no escape. It is a gate, and a
 * gate with a way around it records nothing worth having. It is also deliberately not a hard
 * server-side block on every API call: this is a care roster, and a consent check that can lock a
 * support worker out of their shifts mid-visit would do more harm than the gate prevents. The
 * record is what has legal value, and this is where the record is made.
 */
export default function PolicyAcceptanceGate() {
  const [status, setStatus] = useState(null);   // null = not loaded / nothing owed
  const [ticked, setTicked] = useState(false);
  const [saving, setSaving] = useState(false);

  const check = useCallback(async () => {
    try {
      const { data } = await api.get('/compliance/policy-status');
      setStatus(data && !data.accepted ? data : null);
    } catch {
      // Fail open — never trap someone behind a gate we cannot load.
      setStatus(null);
    }
  }, []);

  useEffect(() => { check(); }, [check]);

  const accept = async () => {
    if (!ticked || saving) return;
    setSaving(true);
    try {
      const { data } = await api.post('/compliance/accept-policies', { documents: status?.missing });
      if (data?.accepted) { setStatus(null); toast.success('Thank you — recorded'); }
      else { toast.error('That did not record. Please try again.'); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not record your acceptance. Please try again.');
    } finally { setSaving(false); }
  };

  if (!status) return null;

  const needsTerms = status.missing?.includes('terms');
  const needsPrivacy = status.missing?.includes('privacy');
  const both = needsTerms && needsPrivacy;
  const urls = status.urls || {};
  const v = status.versions || {};

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" />
      <div role="dialog" aria-modal="true" aria-labelledby="policy-gate-title"
           className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg">
        <div className="px-6 pt-6 pb-4 flex items-start gap-3 border-b border-slate-100">
          <span className="mt-0.5 p-2 rounded-xl bg-primary-50 text-primary-600"><ShieldCheck size={18} /></span>
          <div>
            <h2 id="policy-gate-title" className="text-base font-semibold text-slate-900">
              {both ? 'Please review our Terms and Privacy Policy'
                    : needsTerms ? 'Our Terms of Use have been updated'
                                 : 'Our Privacy Policy has been updated'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              A one-off step. We record that you accepted, and which version.
            </p>
          </div>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-slate-600">
            Rostery handles participant health information, so we set out plainly what we collect, how
            it is protected, where it goes, and what our AI features do and do not decide.
          </p>

          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100">
            {needsTerms && (
              <a href={urls.terms} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 rounded-t-xl">
                <span>
                  <span className="block text-sm font-medium text-slate-800">Terms of Use</span>
                  <span className="block text-xs text-slate-400">Version {v.terms}</span>
                </span>
                <ExternalLink size={14} className="text-slate-400 shrink-0" />
              </a>
            )}
            {needsPrivacy && (
              <a href={urls.privacy} target="_blank" rel="noopener noreferrer"
                 className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-slate-50 last:rounded-b-xl">
                <span>
                  <span className="block text-sm font-medium text-slate-800">Privacy Policy</span>
                  <span className="block text-xs text-slate-400">Version {v.privacy}</span>
                </span>
                <ExternalLink size={14} className="text-slate-400 shrink-0" />
              </a>
            )}
          </div>

          <label htmlFor="policy-gate-accept" className="flex items-start gap-2.5 text-sm text-slate-700 cursor-pointer">
            <input id="policy-gate-accept" type="checkbox" className="mt-1 shrink-0"
                   checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
            <span>
              I have read and accept the {both ? 'Terms of Use and the Privacy Policy'
                                               : needsTerms ? 'Terms of Use' : 'Privacy Policy'}.
            </span>
          </label>
        </div>

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[11px] text-slate-400">
            Recorded with the date and version for our compliance records.
          </span>
          <button onClick={accept} disabled={!ticked || saving}
                  className="btn-primary btn-sm disabled:opacity-40 disabled:cursor-not-allowed">
            {saving ? 'Recording…' : 'Accept and continue'}
          </button>
        </div>
      </div>
    </div>
  );
}
